import { useEffect, useMemo, useState } from "react";

import { observer } from "mobx-react-lite";

import { UnitPreviewNodes } from "@/components/doc/right-sidebar/active-node-style/fields/UnitPreviewNodes";
import { mapUnitToSetChildren } from "@/components/doc/right-sidebar/active-node-style/fields/unitUtils";
import { Dropdown } from "@/components/sidebar/Dropdown";
import { setNodeChildren } from "@/components/suggestions/utils";
import { DocumentModel } from "@/models/DocumentModel";
import { ConstantNodeModel } from "@/models/nodes/ConstantNodeModel";
import { NumberNodeModel } from "@/models/nodes/NumberNodeModel";
import { VariableNodeModel } from "@/models/nodes/VariableNodeModel";

import type { NodeWithUnit } from "./types";
import type { DropdownProps, DropdownItem } from "@/components/sidebar/Dropdown";
import type { UnitWithType } from "@corca/data";

export type ItemWithUnits = {
  units?: UnitWithType[];
} & DropdownItem;

type Props = {
  node: NodeWithUnit;
  items: ItemWithUnits[];
} & DropdownProps;

function createPreviewNode(previewDoc: DocumentModel, sourceNode: NodeWithUnit): NodeWithUnit {
  if (sourceNode.is(VariableNodeModel)) {
    return previewDoc.create(VariableNodeModel, sourceNode.value);
  }
  if (sourceNode.is(ConstantNodeModel)) {
    return previewDoc.create(ConstantNodeModel, sourceNode.value ?? "");
  }
  return previewDoc.create(NumberNodeModel, sourceNode.value ?? "");
}

export const UnitsDropdown = observer(function UnitsDropdown({ node, items, ...props }: Props) {
  const [previewDoc, setPreviewDoc] = useState<DocumentModel | null>(null);

  const handleOpenChange = (open: boolean) => {
    if (open) {
      setPreviewDoc(new DocumentModel("preview"));
    } else {
      setPreviewDoc(null);
    }
  };

  useEffect(() => {
    return () => {
      setPreviewDoc(null);
    };
  }, []);

  const [previewNodesMap, setPreviewNodesMap] = useState<Map<number, NodeWithUnit[]>>(new Map());

  useEffect(() => {
    if (!previewDoc) {
      return;
    }

    const newMap = new Map<number, NodeWithUnit[]>();
    previewDoc.transact(
      () => {
        items.forEach(({ units }, index) => {
          if (units) {
            if (units.length === 0) {
              const previewNode = createPreviewNode(previewDoc, node);

              previewDoc.textNode.appendChild(previewNode);
              newMap.set(index, [previewNode]);
            } else {
              const nodes = units.slice(0, 2).map((unit) => {
                const previewNode = createPreviewNode(previewDoc, node);

                setNodeChildren(previewDoc, previewNode, mapUnitToSetChildren(unit));
                previewDoc.textNode.appendChild(previewNode);
                return previewNode;
              });
              newMap.set(index, nodes);
            }
          }
        });
      },
      "UnitsDropdownPreview:createNodes",
      { runInAction: true },
    );
    setPreviewNodesMap(newMap);
  }, [items, previewDoc, node]);

  const dropdownItems = useMemo(() => {
    return items.map(({ units: _units, ...item }, index) => {
      const previewNodes = previewNodesMap.get(index) ?? [];
      return {
        ...item,
        hotkey: <UnitPreviewNodes nodes={previewNodes} />,
      };
    });
  }, [items, previewNodesMap]);

  if (dropdownItems.length === 0) {
    return null;
  }

  return <Dropdown {...props} onOpenChange={handleOpenChange} items={dropdownItems} />;
});
